import { echeckScannerDelta } from "../../../utils/constants";
import { echeck_confirm_product_amount_option, echeck_scan_option, orderline_type, warehouselocationtype_type } from "../../../utils/staticEnums";

// Builds a products-view payload of arbitrary size, shaped exactly like the
// getPickingJob response, to measure how the job overview renders at scale.
// Reachable only through the ?echeckPerf=<count> flag outside production builds.
export const buildEcheckPerfTestJob = (productCount) => {
    const locations = ["AC 10 03 B", "15-01-B", "BULK 04 12", "AC 02 07 A"];
    const products = [];

    for (let i = 0; i < productCount; i++) {
        const barcode = `87100000${String(i).padStart(5, "0")}`;
        const pickingLineCount = (i % 3) + 1;
        const pickingLines = [];

        for (let j = 0; j < pickingLineCount; j++) {
            pickingLines.push({
                warehouseLocationId: i * 10 + j,
                location: locations[(i + j) % locations.length],
                type: j === 0 ? warehouselocationtype_type.GENERAL : warehouselocationtype_type.BULK,
                amount: j + 1
            });
        }

        products.push({
            type: orderline_type.DEFAULT,
            amount: (i % 5) + 1,
            amountEchecked: 0,
            singles: [],
            pickingLines,
            product: {
                id: i + 1,
                barcode,
                description: `Perftest product ${i + 1}`,
                sku: `PERF-${i + 1}`,
                secondary_sku: null,
                hsCode: null,
                countryOfOrigin: null,
                isVirtual: false,
                barcodes: [{ id: i + 1, barcode }],
                echeckProductValidation: null,
                relation: { echeckProductValidation: null }
            }
        });
    }

    return {
        success: true,
        viewType: "products",
        products,
        totalItems: products.reduce((total, product) => total + product.amount, 0),
        totalShippedItems: 0,
        totalOrdersShipped: 0,
        totalOrders: productCount,
        pickingJobId: -1,
        pickingJobBarcode: "PERFTEST",
        pickingJobState: "fullypicked",
        pickedBy: "Perftest",
        shippedOrders: [],
        pausedOrders: []
    };
};

// Splits merged orderlines into rows that still need scanning and add-on rows
// flagged with autoEcheckAddon. The latter are placed into the first carrier
// automatically during echeck so they don't need to be scanned or clicked.
export const splitAutoEcheckAddons = (mergedOrderlines) => {
    const autoAddonRows = [];
    const scanRows = [];

    for (const line of (mergedOrderlines ?? [])) {
        if (line.type === orderline_type.ADD_ON && line.product?.autoEcheckAddon === true) {
            autoAddonRows.push({ ...line });
        } else {
            scanRows.push(line);
        }
    }

    return { scanRows, autoAddonRows };
};

// Matches a scanned barcode against the packaging barcodes (tray, case) of the rows that
// still need scanning. Returns the row and the packed amount when that amount still fits the
// remaining amount of the row, or exceedsRemaining when the barcode is known but does not fit.
export const findPackagingBarcodeRow = (rows, value) => {
    const matches = [];

    for (const row of (rows ?? [])) {
        const packaging = row.product?.packagingBarcodes?.find(p => String(p.barcode).trim() === value);
        if (packaging) {
            matches.push({ row, amount: parseInt(packaging.amount) });
        }
    }

    if (matches.length === 0) {
        return null;
    }

    const fitting = matches.filter(m => m.amount > 0 && m.amount <= m.row.amount);
    // Prefer default rows over add-ons, the same rule the product barcode match applies
    const preferred = fitting.find(m => m.row.type !== orderline_type.ADD_ON) ?? fitting[0];

    if (preferred) {
        return preferred;
    }

    return { row: null, amount: matches[0].amount, remaining: matches[0].row.amount, exceedsRemaining: true };
};

// Resolves the destination country (alpha-2 code) for an echeck order. The
// country is carried on the shipping address; deliveryCountry is preferred when
// present so callers can pass a flattened order shape too.
export const getOrderDeliveryCountry = (order) => {
    return order?.deliveryCountry || order?.shippingAddress?.country;
};

// Filters a shipper's profiles down to those valid for the order's delivery
// country. A profile with a non-empty standardOptions.allowedCountries is only
// kept when the delivery country is in that list (case-insensitive). Profiles
// without an allowedCountries list are always kept.
export const filterShipperProfilesByDeliveryCountry = (shipperProfiles, deliveryCountry) => {
    if (!Array.isArray(shipperProfiles)) return [];

    const country = deliveryCountry?.trim().toLowerCase();

    return shipperProfiles.filter(profile => {
        const allowedCountries = profile?.standardOptions?.allowedCountries;
        if (!Array.isArray(allowedCountries) || allowedCountries.length === 0) {
            return true;
        }
        if (!country) return true;
        return allowedCountries.some(allowed => allowed?.trim().toLowerCase() === country);
    });
};

// The names of the virtual products a row is a component of. A merged row carries the names
// of all its order lines, a single order line only knows its own parent product.
// Scan or click a product: the product's own setting leads, "default" falls back to its relation.
export const getProductValidation = (product) => {
    if (product?.echeckProductValidation && product.echeckProductValidation !== echeck_scan_option.DEFAULT) {
        return product.echeckProductValidation;
    }
    return product?.relation?.echeckProductValidation;
};

// Whether a single-line order with more than one product asks to confirm the packed amount.
// The product setting leads, then its relation, then the general setting echeck_confirm_product_amount.
export const shouldConfirmProductAmount = (order, generalSetting) => {
    if (order?.amountOfLines !== 1 || !(order?.amountOfProducts > 1)) {
        return false;
    }

    const line = order.orderlines?.find(orderline => orderline.type !== orderline_type.ADD_ON) ?? order.orderlines?.[0];
    for (const value of [line?.product?.echeckConfirmProductAmount, line?.product?.relation?.echeckConfirmProductAmount]) {
        if (value === echeck_confirm_product_amount_option.ALWAYS) return true;
        if (value === echeck_confirm_product_amount_option.NEVER) return false;
    }
    return generalSetting;
};

// Whether the order's totalPrice exceeds the echeck order value threshold of its relation; an empty threshold is off.
export const exceedsOrderValueThreshold = (order) => {
    const threshold = order?.relation?.echeckOrderValueThreshold;
    if (threshold === null || threshold === undefined || threshold === "") {
        return false;
    }
    return Number(order?.totalPrice) > Number(threshold);
};

export const getVirtualProductNames = (row) => {
    return row?.virtualProductNames ?? [row?.parentProduct?.description].filter(Boolean);
};

export const mergeVirtualProductNames = (row, line) => {
    return [...new Set([...getVirtualProductNames(row), ...getVirtualProductNames(line)])];
};

// The picking lines of a to-scan row summed per location, in the route order the backend sorted them in
export const getPickingLocations = (row) => {
    const locations = new Map();

    for (const pickingLine of (row?.pickingLines ?? [])) {
        const warehouseLocation = pickingLine.stock?.warehouseLocation;
        if (!warehouseLocation) continue;

        if (!locations.has(warehouseLocation.id)) {
            locations.set(warehouseLocation.id, {
                warehouseLocationId: warehouseLocation.id,
                location: warehouseLocation.location,
                type: warehouseLocation.type,
                amount: 0
            });
        }
        locations.get(warehouseLocation.id).amount += pickingLine.amount;
    }

    return [...locations.values()];
};

export const parseDragId = (id) => {
  const parts = id.split('-');

  if (parts[0] === 'carrier') {
    // Format: carrier-{carrierIndex}-row-{rowIndex}
    return {
      bundleIndex: null,
      carrierIndex: Number(parts[1]),
      rowIndex: Number(parts[3]),
    };
  } else if (parts[0] === 'bundle') {
    // Format: bundle-{bundleIndex}-carrier-{carrierIndex}-row-{rowIndex}
    return {
      bundleIndex: Number(parts[1]),
      carrierIndex: Number(parts[3]),
      rowIndex: Number(parts[5]),
    };
  }
}


const getCarrier = (state, bundleIndex, carrierIndex) => {
    if (!Number.isFinite(bundleIndex)) {
        return state[carrierIndex];
    }
    const bundle = state[bundleIndex];
    if (!bundle?.carriers || !Array.isArray(bundle.carriers)) {
        console.warn("Invalid bundle access at index", bundleIndex, bundle);
        return null;
    }
    return bundle.carriers[carrierIndex];
}

export const moveRow = (carriers, from, to, forceThrough = false,quantityToMove = 1) => {
    // Deep clone carriers & their rows (to avoid mutation)
    const newCarriers = carriers.map((carrier) => {
        if (carrier.bundle && Array.isArray(carrier.carriers)) {
        return {
            ...carrier,
            carriers: carrier.carriers.map((c) => ({
                ...c,
                rows: [...c.rows],
            })),
        };
        }
        return { ...carrier, rows: [...carrier.rows] };
    });

    const fromCarrier = getCarrier(newCarriers, from.bundleIndex, from.carrierIndex);
    const toCarrier = getCarrier(newCarriers, to.bundleIndex, to.carrierIndex);


    // Safety check
    if (!fromCarrier || !toCarrier) return newCarriers;

    const fromRow = fromCarrier.rows[from.rowIndex];
    if (!fromRow) return newCarriers;

    if(fromRow.amount >= 2 && !forceThrough){
        return fromRow;
    }

    // Moving within the same carrier (just reorder)
    if (
        from.bundleIndex === to.bundleIndex &&
        from.carrierIndex === to.carrierIndex
    ) {
        const rowsCopy = [...fromCarrier.rows];
        const [movedRow] = rowsCopy.splice(from.rowIndex, 1);
        rowsCopy.splice(to.rowIndex, 0, movedRow);

        // Update rows on the carrier
        if (!Number.isFinite(from.bundleIndex)) {
            newCarriers[from.carrierIndex] = {
                ...fromCarrier,
                rows: rowsCopy,
            };
        } else {
            newCarriers[from.bundleIndex].carriers[from.carrierIndex] = {
                ...fromCarrier,
                rows: rowsCopy,
            };
        }

        return newCarriers;
    }

    // Moving between different carriers (handle amounts)
    const itemToMove = fromCarrier.rows[from.rowIndex];

    if (itemToMove.amount <= 0) return newCarriers;

    // don't move more than we have
    const moveAmount = Math.min(itemToMove.amount, quantityToMove); 


    // Decrease amount in fromCarrier
    const updatedFromRows = [...fromCarrier.rows];
    updatedFromRows[from.rowIndex] = {
        ...itemToMove,
        amount: itemToMove.amount - moveAmount,
    };

    // Remove if amount hits zero
    if (updatedFromRows[from.rowIndex].amount === 0) {
        updatedFromRows.splice(from.rowIndex, 1);
    }

    // Increase or add to toCarrier
    const updatedToRows = [...toCarrier.rows];
    
    // const targetIndex = updatedToRows.findIndex(
    //     (row) => row.product.id === itemToMove.product.id
    // );
    const targetIndex = updatedToRows.findIndex(row => {
        const rowSerial = row.serialnumbers?.[0];
        const itemSerial = itemToMove.serialnumbers?.[0];

        const serialMatch = !rowSerial || !itemSerial || rowSerial === itemSerial;

        const externalIdMatch =
            (row.externalId && itemToMove.externalId)
                ? row.externalId === itemToMove.externalId
                : true;

        const typeMatch = row.type === itemToMove.type;

        return row.product.id === itemToMove.product.id && serialMatch && externalIdMatch && typeMatch;
    });

    if (targetIndex >= 0) {
        updatedToRows[targetIndex] = {
            ...updatedToRows[targetIndex],
            amount: updatedToRows[targetIndex].amount + moveAmount,
        };
    } else {
        updatedToRows.splice(
        Number.isFinite(to.rowIndex) && to.rowIndex >= 0 ? to.rowIndex : updatedToRows.length,
        0,
        {
            ...itemToMove,
            amount: moveAmount,
        }
        );
    }

    // Update carriers in newCarriers
    if (!Number.isFinite(from.bundleIndex)) {
        newCarriers[from.carrierIndex] = {
            ...fromCarrier,
            rows: updatedFromRows,
        };
    } else {
        newCarriers[from.bundleIndex].carriers[from.carrierIndex] = {
            ...fromCarrier,
            rows: updatedFromRows,
        };
    }

    if (!Number.isFinite(to.bundleIndex)) {
        newCarriers[to.carrierIndex] = {
            ...toCarrier,
            rows: updatedToRows,
        };
    } else {
        newCarriers[to.bundleIndex].carriers[to.carrierIndex] = {
            ...toCarrier,
            rows: updatedToRows,
        };
    }

    return newCarriers;
};

const isSameCarrierRow = (a, b) => {
    const serialMatch = !a.serialnumbers?.[0] || !b.serialnumbers?.[0] || a.serialnumbers[0] === b.serialnumbers[0];
    const externalIdMatch = (a.externalId && b.externalId) ? a.externalId === b.externalId : true;
    return a.product?.barcode === b.product?.barcode && a.type === b.type && serialMatch && externalIdMatch;
};

// Puts every product that still waits on a scan in the first carrier, so an order can be
// finalized without scanning. Each row is split over its remaining original orderlines by
// externalId, the way a scan does, so a carrier row keeps pointing at one orderline. Add-ons
// that were placed automatically are not in toScanRows and stay where they are.
export const addRemainingRowsToFirstCarrier = (carriers, toScanRows, originalOrderlines) => {
    if (!carriers?.length || !toScanRows?.length) return carriers;

    const remainingOriginals = (originalOrderlines ?? []).map(line => ({ ...line }));
    const rowsToAdd = [];

    for (const row of toScanRows) {
        let open = row.amount;
        for (const original of remainingOriginals) {
            if (open <= 0) break;
            const matches = original.amount > 0 &&
                original.product?.barcode === row.product?.barcode &&
                original.type === row.type &&
                (!original.serialnumbers?.[0] || !row.serialnumbers?.[0] || original.serialnumbers[0] === row.serialnumbers[0]);
            if (!matches) continue;

            const take = Math.min(open, original.amount);
            rowsToAdd.push({ ...row, externalId: original.externalId, amount: take });
            original.amount -= take;
            open -= take;
        }
        if (open > 0) {
            rowsToAdd.push({ ...row, amount: open });
        }
    }

    const addRows = (target) => {
        const rows = [...(target.rows ?? [])];
        for (const rowToAdd of rowsToAdd) {
            const index = rows.findIndex(row => isSameCarrierRow(row, rowToAdd));
            if (index !== -1) {
                rows[index] = { ...rows[index], amount: rows[index].amount + rowToAdd.amount };
            } else {
                rows.push(rowToAdd);
            }
        }
        return { ...target, rows };
    };

    const [first, ...rest] = carriers;
    const filledFirst = first.bundle && first.carriers?.length > 0
        ? { ...first, carriers: [addRows(first.carriers[0]), ...first.carriers.slice(1)] }
        : addRows(first);

    return [filledFirst, ...rest];
};

export const purgeEmptyCarriersAndBundles = (carriers) => {
    return carriers.map((carrier) => {
        // Handle bundles (which contain sub-carriers)
        if (carrier.bundle && Array.isArray(carrier.carriers)) {
            const cleanedSubCarriers = carrier.carriers.map((sub) => {
                // Remove addon rows from sub-carriers
                const filteredRows = (sub.rows || []).filter(
                    (row) => row.type !== orderline_type.ADD_ON
                );

                // Return updated sub-carrier only if it has non-addon rows
                if (filteredRows.length > 0) {
                    return {
                        ...sub,
                        rows: filteredRows,
                    };
                }

                return null; // Purge empty sub-carrier
            }).filter(Boolean); // Remove nulls (empty sub-carriers)

            // Only keep the bundle if it still has non-empty sub-carriers
            if (cleanedSubCarriers.length > 0) {
                return {
                    ...carrier,
                    carriers: cleanedSubCarriers,
                };
            }

            return null; // Purge empty bundle
        }

        const filteredRows = (carrier.rows || []).filter(
            (row) => row.type !== orderline_type.ADD_ON
        );

        if (filteredRows.length > 0) {
            return {
                ...carrier,
                rows: filteredRows,
            };
        }

        return null; // Purge empty carrier
    }).filter(Boolean); // Remove nulls
};

export const bundleCarriers = (carriersForBundle,carrierInfo,carriers) => {
    let strippedShipperOverride = false;
    const carriersToBundle = carriersForBundle.map(index => {
        const c = carriers[index];
        if(c?.shipperId || c?.shipperProfileId){
            strippedShipperOverride = true;
            const { shipperId, shipperProfileId, shipperName, shipperProfileName, shipperType, ...rest } = c;
            return rest;
        }
        return c;
    });

    const newBundledCarrier = {
        carrierId: carrierInfo.id,
        bundle: true,
        carrierName: carrierInfo.name,
        type: carrierInfo.type,
        deliveryOption: null,
        dimensions: {
            width: carrierInfo.width,
            height: carrierInfo.height,
            length: carrierInfo.length,
            weight: carrierInfo.weight,
            isFlexible: carrierInfo.isFlexible,
            type:carrierInfo.type,
        },
        carriers: carriersToBundle,
    };

    const updatedCarriers = [...carriers];

    const sortedIndexes = [...carriersForBundle].sort((a, b) => a - b);
    const lowestIndex = sortedIndexes[0];

    updatedCarriers[lowestIndex] = newBundledCarrier;

    for (let i = sortedIndexes.length - 1; i > 0; i--) {
        updatedCarriers.splice(sortedIndexes[i], 1);
    }
    return { carriers: updatedCarriers, strippedShipperOverride };
}

// Label of a Bol LvB pickup time slot in Dutch time, e.g. "30-09-2026 08:00 - 12:00"
export const formatPickupTimeSlot = (slot) => {
    const date = new Intl.DateTimeFormat('nl-NL', { timeZone: 'Europe/Amsterdam', day: '2-digit', month: '2-digit', year: 'numeric' });
    const time = new Intl.DateTimeFormat('nl-NL', { timeZone: 'Europe/Amsterdam', hour: '2-digit', minute: '2-digit', hour12: false });
    const from = new Date(slot.fromDateTime);
    return `${date.format(from)} ${time.format(from)} - ${time.format(new Date(slot.untilDateTime))}`;
};

// Whether an order can be finalized: every product is scanned, a shipper and profile are
// chosen and, when carriers are mandatory, every carrier holding products has one assigned.
export const isReadyToFinalize = (toScanRows, carriers, selectedShipper, selectedShipperProfile, carrierMandatory) => {
    if (toScanRows?.length !== 0 || !selectedShipper || !selectedShipperProfile) return false;
    if (!carrierMandatory) return true;
    return !carriers.some(carrier => {
        if (carrier.bundle) {
            return carrier.carriers.some(sub => sub.carrierId === null);
        }
        return carrier.carrierId === null && carrier.rows.length > 0;
    });
};

// Bol makes a label per delivery time frame, so the products of an order with more of them go over
// one carrier per group. Carriers the operator already chose are kept, the rest stay empty so the
// real box gets scanned. A row without a Bol order item, like an add-on, stays in the first carrier.
// Returns null when a group has no rows, then there is nothing that can be moved with certainty.
export const splitCarriersByOrderItemGroups = (carriers, groups) => {
    const flatCarriers = carriers.flatMap(carrier => (
        carrier.bundle && Array.isArray(carrier.carriers) ? carrier.carriers : [carrier]
    ));
    const rows = flatCarriers.flatMap(carrier => carrier.rows ?? []);
    const isInGroup = (row, group) => group.orderItemIds.some(id => String(id) === String(row.externalId));

    const rowsPerGroup = groups.map(group => rows.filter(row => isInGroup(row, group)));
    if (rowsPerGroup.some(groupRows => groupRows.length === 0)) {
        return null;
    }

    const otherRows = rows.filter(row => !groups.some(group => isInGroup(row, group)));
    const chosenCarriers = flatCarriers.filter(carrier => carrier.carrierId !== null || carrier.rows?.length > 0);

    return rowsPerGroup.map((groupRows, index) => ({
        carrierId: null,
        carrierName: "",
        totalProducts: 0,
        totalWeight: 0,
        deliveryOption: null,
        dimensions: null,
        ...chosenCarriers[index],
        bundle: false,
        rows: index === 0 ? [...groupRows, ...otherRows] : groupRows,
    }));
};

// The products per label when every carrier got one label. Anything else, like an extra return
// label, means the labels and carriers do not line up.
export const getRowsPerLabel = (carriers, labelCount) => {
    if (carriers?.length !== labelCount) {
        return null;
    }

    return carriers.map(carrier => (
        carrier.bundle && Array.isArray(carrier.carriers)
            ? carrier.carriers.flatMap(sub => sub.rows ?? [])
            : carrier.rows ?? []
    ));
};

// Puts every product that still has to be scanned in the first carrier, used when a shipped order is restarted
export const prefillRestartedOrder = (carriers, toScanRows, originalOrderlines) => ({
    carriers: addRemainingRowsToFirstCarrier(carriers, toScanRows, originalOrderlines),
    toScanRows: [],
    originalOrderlines: [],
});

// A scanner types much faster than a person, but over Bluetooth a single keystroke
// can arrive late, so judge the median gap instead of requiring every gap to be fast.
export const isScannerInput = (deltas) => {
    if (deltas.length === 0) return true;
    const sorted = [...deltas].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    const median = sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
    return median < echeckScannerDelta;
};
